import { useState } from "react";
import type { LocalStorageKey } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import {
  getLocalStorageJSON,
  setLocalStorageJSON,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";

type DateRangeFilters = {
  dateFrom?: Date;
  dateTo?: Date;
};

// Serialized format for localStorage (dates as ISO strings)
type SerializedFilters<TFilters extends DateRangeFilters> = Omit<
  TFilters,
  "dateFrom" | "dateTo"
> & {
  dateFrom?: string;
  dateTo?: string;
};

const serializeFilters = <TFilters extends DateRangeFilters>(
  filters: TFilters,
): SerializedFilters<TFilters> => ({
  ...filters,
  dateFrom: filters.dateFrom?.toISOString(),
  dateTo: filters.dateTo?.toISOString(),
});

const deserializeFilters = <TFilters extends DateRangeFilters>(
  serialized: SerializedFilters<TFilters>,
): TFilters =>
  ({
    ...serialized,
    dateFrom: serialized.dateFrom ? new Date(serialized.dateFrom) : undefined,
    dateTo: serialized.dateTo ? new Date(serialized.dateTo) : undefined,
  }) as TFilters;

const loadFiltersFromStorage = <TFilters extends DateRangeFilters>(
  storageKey: LocalStorageKey,
): TFilters | null => {
  const stored = getLocalStorageJSON<SerializedFilters<TFilters>>(storageKey);
  if (!stored) {
    return null;
  }
  return deserializeFilters(stored);
};

const saveFiltersToStorage = <TFilters extends DateRangeFilters>(
  storageKey: LocalStorageKey,
  filters: TFilters,
): void => {
  setLocalStorageJSON(storageKey, serializeFilters(filters));
};

type Props<TFilters extends DateRangeFilters> = {
  storageKey: LocalStorageKey;
  initialFilters: TFilters;
};

export const usePersistedFilters = <TFilters extends DateRangeFilters>({
  storageKey,
  initialFilters,
}: Props<TFilters>) => {
  const [filters, setFilters] = useState<TFilters>(
    () => loadFiltersFromStorage<TFilters>(storageKey) ?? initialFilters,
  );

  const applyFilters = (newFilters: TFilters) => {
    setFilters(newFilters);
    saveFiltersToStorage(storageKey, newFilters);
  };

  return {
    filters,
    // Updates state without persisting to localStorage
    setFilters,
    applyFilters,
  };
};
